/**
 * Global app lock store – maintenance mode and server-down.
 * When locked, the app renders only MaintenanceScreen and blocks all routes.
 */

export type AppLockReason = 'maintenance' | 'server-down'

interface AppLockState {
  isLocked: boolean
  reason: AppLockReason | null
  expectedUptime: string | undefined
  message: string | undefined
}

const initialState: AppLockState = {
  isLocked: false,
  reason: null,
  expectedUptime: undefined,
  message: undefined,
}

let state: AppLockState = { ...initialState }
const listeners: Set<() => void> = new Set()

function getState(): AppLockState {
  return state
}

function setState(next: Partial<AppLockState>) {
  state = { ...state, ...next }
  listeners.forEach((fn) => fn())
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/**
 * Lock the app (maintenance or server-down). Only MaintenanceScreen will render.
 */
export function lockApp(
  reason: AppLockReason,
  message?: string,
  expectedUptime?: string
): void {
  setState({
    isLocked: true,
    reason,
    message: message ?? undefined,
    expectedUptime: expectedUptime ?? undefined,
  })
}

/**
 * Unlock the app so routes render again.
 */
export function unlockApp(): void {
  setState(initialState)
}

export const appLockStore = {
  getState,
  subscribe,
  lockApp,
  unlockApp,
}
